import { Section, SectionLayoutConfig } from '@/features/projects/types';
import { EditableCtaWrapper } from '../EditableCtaWrapper';
import type { FormConfig } from '../types';
import { InteractiveWidgetForm } from '../forms/InteractiveWidgetForm';
import { HeroLayoutId, ButtonStyleId, BUTTON_STYLES, getButtonStyles, DEFAULT_BUTTON_STYLE } from '@/constants/layoutTypes';

interface HeroSectionProps {
    content: {
        headline: string;
        subheadline: string;
        cta: string;
        ctaLink?: string;
        ctaAction?: 'scroll' | 'url';
    };
    primaryColor: string;
    sizeClasses: { heading: string; subheading: string; body: string; small: string };
    isEditing: boolean;
    onEdit: (section: string, field: string, value: string) => void;
    sections?: Section[];
    layout?: HeroLayoutId;
    layoutConfig?: SectionLayoutConfig;
    buttonStyle?: ButtonStyleId;
    onButtonStyleChange?: (style: ButtonStyleId) => void;
    badgeText?: string | null;
    showBadge?: boolean;
    onCtaClick?: (source: 'hero') => void;
    formConfig?: FormConfig;
    onSubmit?: (data: { email: string; firstName?: string; lastName?: string; phone?: string; customAnswer?: string }) => Promise<{ success: boolean; data?: any }>;
}

// ============================================================================
// UTILITY: Background style resolver
// ============================================================================
function getHeroBgStyle(
    primaryColor: string,
    layoutConfig?: SectionLayoutConfig,
    layout?: HeroLayoutId
): React.CSSProperties {
    const bgType = layoutConfig?.heroBackground || 'gradient';
    const customColor = layoutConfig?.heroBackgroundColor;

    if (layout === 'gradient-bold') {
        const darkenColor = (hex: string, percent: number): string => {
            const num = parseInt(hex.replace('#', ''), 16);
            const amt = Math.round(2.55 * percent);
            const R = Math.max((num >> 16) - amt, 0);
            const G = Math.max((num >> 8 & 0x00FF) - amt, 0);
            const B = Math.max((num & 0x0000FF) - amt, 0);
            return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
        };
        if (bgType === 'white') return { background: 'white' };
        if (bgType === 'colored' && customColor) {
            const end = darkenColor(customColor, 30);
            return { background: `linear-gradient(135deg, ${customColor} 0%, ${end} 100%)` };
        }
        const end = darkenColor(primaryColor, 30);
        return { background: `linear-gradient(135deg, ${primaryColor} 0%, ${end} 100%)` };
    }

    if (layout === 'split') {
        if (bgType === 'colored' && customColor) return { background: customColor };
        return { background: 'white' };
    }

    if (bgType === 'white') return { background: 'white' };

    if (bgType === 'colored' && customColor) {
        return { background: `linear-gradient(160deg, ${customColor} 0%, ${customColor}dd 40%, ${customColor}aa 100%)` };
    }

    return { background: `linear-gradient(160deg, ${primaryColor}06 0%, ${primaryColor}12 50%, ${primaryColor}04 100%)` };
}

function isLightMode(layout?: HeroLayoutId, bgType?: string): boolean {
    if (layout === 'gradient-bold' && bgType !== 'white') return true;
    return false;
}

// ============================================================================
// SHARED DECORATION COMPONENTS
// ============================================================================

function GradientOrbs({ primaryColor, variant = 'default' }: { primaryColor: string; variant?: 'default' | 'dark' | 'warm' | 'cool' }) {
    if (variant === 'dark') {
        return (
            <>
                {/* Top-right glow */}
                <div
                    className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-[120px] opacity-[0.15] pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)' }}
                />
                {/* Bottom-left glow */}
                <div
                    className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full blur-[100px] opacity-[0.1] pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #a855f7 0%, transparent 70%)' }}
                />
                {/* Center subtle glow */}
                <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full blur-[150px] opacity-[0.06] pointer-events-none"
                    style={{ background: 'radial-gradient(ellipse, #818cf8 0%, transparent 70%)' }}
                />
            </>
        );
    }

    if (variant === 'warm') {
        return (
            <>
                <div
                    className="absolute -top-20 right-1/4 w-[500px] h-[500px] rounded-full blur-[100px] opacity-[0.08] pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)' }}
                />
                <div
                    className="absolute -bottom-32 left-1/4 w-[400px] h-[400px] rounded-full blur-[80px] opacity-[0.06] pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #f97316 0%, transparent 70%)' }}
                />
            </>
        );
    }

    if (variant === 'cool') {
        return (
            <>
                <div
                    className="absolute -top-32 left-1/3 w-[600px] h-[600px] rounded-full blur-[120px] opacity-[0.1] pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${primaryColor}60 0%, transparent 70%)` }}
                />
                <div
                    className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] rounded-full blur-[100px] opacity-[0.08] pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #ec489940 0%, transparent 70%)' }}
                />
                <div
                    className="absolute top-1/4 -right-20 w-[400px] h-[400px] rounded-full blur-[80px] opacity-[0.06] pointer-events-none"
                    style={{ background: 'radial-gradient(circle, #3b82f640 0%, transparent 70%)' }}
                />
            </>
        );
    }

    // Default: subtle single-color orbs
    return (
        <>
            <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full blur-[120px] opacity-[0.06] pointer-events-none"
                style={{ background: `radial-gradient(ellipse, ${primaryColor} 0%, transparent 70%)` }}
            />
            <div
                className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full blur-[80px] opacity-[0.05] pointer-events-none"
                style={{ background: `radial-gradient(circle, ${primaryColor}80 0%, transparent 70%)` }}
            />
        </>
    );
}

function NoiseTexture({ opacity = 0.03 }: { opacity?: number }) {
    return (
        <div
            className="absolute inset-0 pointer-events-none"
            style={{
                opacity,
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                backgroundSize: '128px 128px',
            }}
        />
    );
}

function DotGrid({ color = 'currentColor', spacing = 24, size = 1, opacity = 0.04 }: { color?: string; spacing?: number; size?: number; opacity?: number }) {
    return (
        <div
            className="absolute inset-0 pointer-events-none"
            style={{
                opacity,
                backgroundImage: `radial-gradient(circle at ${size}px ${size}px, ${color} ${size}px, transparent 0)`,
                backgroundSize: `${spacing}px ${spacing}px`,
            }}
        />
    );
}

// ============================================================================
// SHARED UI COMPONENTS
// ============================================================================

// CTA Button
function HeroCtaButton({
    content,
    primaryColor,
    isEditing,
    onEdit,
    sections,
    buttonStyle = DEFAULT_BUTTON_STYLE,
    onStyleChange,
    onCtaClick,
    variant = 'default'
}: {
    content: HeroSectionProps['content'];
    primaryColor: string;
    isEditing: boolean;
    onEdit: HeroSectionProps['onEdit'];
    sections: Section[];
    buttonStyle?: ButtonStyleId;
    onStyleChange?: (style: ButtonStyleId) => void;
    onCtaClick?: (source: 'hero') => void;
    variant?: 'default' | 'light';
}) {
    const styleOption = BUTTON_STYLES.find(s => s.id === buttonStyle) || BUTTON_STYLES[0];
    const inlineStyles = variant === 'light'
        ? { backgroundColor: 'white', color: primaryColor }
        : getButtonStyles(buttonStyle, primaryColor);

    return (
        <EditableCtaWrapper
            text={content.cta}
            link={content.ctaLink}
            action={content.ctaAction}
            buttonStyle={buttonStyle}
            primaryColor={primaryColor}
            isEditing={isEditing}
            sections={sections}
            onUpdate={(text, link, action) => {
                onEdit('hero', 'cta', text);
                onEdit('hero', 'ctaLink', link);
                onEdit('hero', 'ctaAction', action);
            }}
            onStyleChange={onStyleChange}
        >
            <button
                className={`${variant === 'light' ? 'px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all text-lg' : `${styleOption.className} text-lg`}`}
                style={inlineStyles}
                onClick={() => {
                    onCtaClick?.('hero');
                    if (content.ctaAction === 'url' && content.ctaLink) {
                        window.open(content.ctaLink, '_blank');
                    } else if (content.ctaLink) {
                        document.getElementById(content.ctaLink)?.scrollIntoView({ behavior: 'smooth' });
                    }
                }}
            >
                {content.cta}
            </button>
        </EditableCtaWrapper>
    );
}

function HeroActionArea({
    content,
    primaryColor,
    isEditing,
    onEdit,
    sections,
    layoutConfig,
    buttonStyle,
    onStyleChange,
    onCtaClick,
    formConfig,
    onSubmit,
    variant = 'default',
}: {
    content: HeroSectionProps['content'];
    primaryColor: string;
    isEditing: boolean;
    onEdit: HeroSectionProps['onEdit'];
    sections: Section[];
    layoutConfig?: SectionLayoutConfig;
    buttonStyle?: ButtonStyleId;
    onStyleChange?: (style: ButtonStyleId) => void;
    onCtaClick?: (source: 'hero') => void;
    formConfig?: FormConfig;
    onSubmit?: HeroSectionProps['onSubmit'];
    variant?: 'default' | 'light';
}) {
    if (layoutConfig?.embedSignupInHero && formConfig) {
        return (
            <InteractiveWidgetForm
                formConfig={formConfig}
                primaryColor={primaryColor}
                onSubmit={onSubmit}
                formTemplate={(layoutConfig.heroFormTemplate as any) || 'centered'}
                hideChrome
            />
        );
    }

    return (
        <HeroCtaButton
            content={content}
            primaryColor={primaryColor}
            isEditing={isEditing}
            onEdit={onEdit}
            sections={sections}
            buttonStyle={buttonStyle}
            onStyleChange={onStyleChange}
            onCtaClick={onCtaClick}
            variant={variant}
        />
    );
}

// Avatar pool
const AVATAR_POOL = [
    { initials: 'AK', bg: '#6366f1', gradient: 'from-indigo-400 to-indigo-600' },
    { initials: 'SM', bg: '#ec4899', gradient: 'from-pink-400 to-pink-600' },
    { initials: 'RJ', bg: '#f59e0b', gradient: 'from-amber-400 to-amber-600' },
    { initials: 'JL', bg: '#10b981', gradient: 'from-emerald-400 to-emerald-600' },
    { initials: 'NK', bg: '#8b5cf6', gradient: 'from-violet-400 to-violet-600' },
    { initials: 'TP', bg: '#ef4444', gradient: 'from-red-400 to-red-600' },
    { initials: 'DW', bg: '#0ea5e9', gradient: 'from-sky-400 to-sky-600' },
    { initials: 'LM', bg: '#f97316', gradient: 'from-orange-400 to-orange-600' },
    { initials: 'KS', bg: '#14b8a6', gradient: 'from-teal-400 to-teal-600' },
    { initials: 'PG', bg: '#a855f7', gradient: 'from-purple-400 to-purple-600' },
    { initials: 'YZ', bg: '#e11d48', gradient: 'from-rose-400 to-rose-600' },
    { initials: 'BC', bg: '#2563eb', gradient: 'from-blue-400 to-blue-600' },
];

function pickAvatars(primaryColor: string, count: number = 5, extraSeed: number = 0) {
    const colorSeed = primaryColor.replace('#', '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const seed = colorSeed + extraSeed * 37;
    const shuffled = [...AVATAR_POOL].sort((a, b) => {
        const ha = (a.bg.charCodeAt(1) * 31 + a.initials.charCodeAt(0) * 7 + seed) % 100;
        const hb = (b.bg.charCodeAt(1) * 31 + b.initials.charCodeAt(0) * 7 + seed) % 100;
        return ha - hb;
    });
    const picked = shuffled.slice(0, count);
    picked[0] = { ...picked[0], bg: primaryColor };
    return picked;
}

// Social proof trust bar — upgraded with count badge and gradient avatars
function TrustBar({ primaryColor, variant = 'default', text, avatarSeed = 0 }: { primaryColor: string; variant?: 'default' | 'light'; text?: string; avatarSeed?: number }) {
    const isDark = variant === 'light';
    const textColor = isDark ? 'text-white/70' : 'text-gray-500';
    const avatarBorder = isDark ? 'border-white/10' : 'border-white';
    const dotColor = isDark ? 'bg-emerald-400' : 'bg-emerald-500';
    const displayText = text || 'Join early adopters who signed up';
    const avatars = pickAvatars(primaryColor, 5, avatarSeed);
    const countBg = isDark ? 'bg-white/10 text-white/80 border-white/10' : 'bg-gray-100 text-gray-600 border-gray-200/60';

    return (
        <div className="flex flex-col items-center gap-2.5 sm:flex-row sm:gap-3">
            <div className="flex items-center">
                <div className="flex -space-x-2.5">
                    {avatars.map((avatar, i) => (
                        <div
                            key={i}
                            className={`w-9 h-9 rounded-full border-[2.5px] ${avatarBorder} flex items-center justify-center text-white text-[10px] font-bold shadow-sm ring-1 ring-black/[0.03]`}
                            style={{
                                background: `linear-gradient(135deg, ${avatar.bg}dd 0%, ${avatar.bg} 100%)`,
                                zIndex: avatars.length - i,
                            }}
                        >
                            {avatar.initials}
                        </div>
                    ))}
                </div>
                <div className={`ml-0.5 flex h-9 w-9 items-center justify-center rounded-full border ${countBg} text-[11px] font-bold`} style={{ zIndex: 0 }}>
                    +{Math.floor((avatarSeed + 3) * 40 + 80)}
                </div>
            </div>
            <div className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 ${dotColor} rounded-full animate-pulse`} />
                <span className={`text-sm font-medium ${textColor}`}>{displayText}</span>
            </div>
        </div>
    );
}

// Badge component — upgraded with shimmer animation
function HeroBadge({
    text,
    primaryColor,
    variant = 'default'
}: {
    text: string;
    primaryColor: string;
    variant?: 'default' | 'light';
}) {
    if (variant === 'light') {
        return (
            <span className="group relative inline-flex items-center gap-2 mb-6 px-5 py-2 text-sm font-semibold rounded-full bg-white/[0.08] text-white/90 backdrop-blur-md border border-white/[0.12] shadow-lg shadow-black/10 overflow-hidden">
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <span className="relative flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse shadow-sm shadow-emerald-400/50" />
                    {text}
                </span>
            </span>
        );
    }

    return (
        <span className="group relative inline-flex items-center gap-2 mb-6 px-5 py-2 text-sm font-semibold rounded-full overflow-hidden" style={{ backgroundColor: `${primaryColor}12`, color: primaryColor }}>
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" style={{ background: `linear-gradient(90deg, transparent, ${primaryColor}15, transparent)` }} />
            <span className="relative flex items-center gap-2">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: primaryColor }} />
                {text}
            </span>
        </span>
    );
}

// Product mockup placeholder — stylized app UI skeleton
function ProductMockupPlaceholder({ primaryColor }: { primaryColor: string }) {
    return (
        <div className="w-full h-full min-h-[350px] rounded-2xl border border-gray-200/60 bg-white shadow-2xl shadow-gray-200/50 overflow-hidden">
            {/* Title bar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                </div>
                <div className="flex-1 flex justify-center">
                    <div className="w-32 h-4 rounded-full bg-gray-200/80" />
                </div>
            </div>
            {/* Content skeleton */}
            <div className="p-5 space-y-4">
                <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-xl shrink-0" style={{ backgroundColor: `${primaryColor}15` }} />
                    <div className="flex-1 space-y-2">
                        <div className="h-3 w-3/4 rounded-full bg-gray-200" />
                        <div className="h-2.5 w-1/2 rounded-full bg-gray-100" />
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                    {[0, 1, 2].map(i => (
                        <div key={i} className="rounded-xl border border-gray-100 p-3 space-y-2">
                            <div className="h-8 rounded-lg" style={{ backgroundColor: i === 0 ? `${primaryColor}10` : '#f3f4f6' }} />
                            <div className="h-2 w-3/4 rounded-full bg-gray-200" />
                            <div className="h-2 w-1/2 rounded-full bg-gray-100" />
                        </div>
                    ))}
                </div>
                <div className="space-y-2.5">
                    {[0, 1, 2].map(i => (
                        <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50/80">
                            <div className="w-8 h-8 rounded-lg bg-gray-200/80 shrink-0" />
                            <div className="flex-1 space-y-1.5">
                                <div className="h-2.5 rounded-full bg-gray-200" style={{ width: `${70 - i * 15}%` }} />
                                <div className="h-2 w-1/3 rounded-full bg-gray-100" />
                            </div>
                            <div className="w-14 h-6 rounded-full" style={{ backgroundColor: i === 0 ? `${primaryColor}15` : '#f3f4f6' }} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// LAYOUT 1: CENTERED
// ============================================================================
function HeroCentered({
    content,
    primaryColor,
    sizeClasses,
    isEditing,
    onEdit,
    sections,
    layoutConfig,
    buttonStyle,
    onButtonStyleChange,
    badgeText = 'Beta',
    showBadge = false,
    onCtaClick,
    formConfig,
    onSubmit
}: Omit<HeroSectionProps, 'layout'> & { layoutConfig?: SectionLayoutConfig }) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-4' : '';
    const showTrustBar = layoutConfig?.showTrustBar !== false;
    const showDecorations = layoutConfig?.showDecorations !== false;
    const bgStyle = getHeroBgStyle(primaryColor, layoutConfig, 'centered');

    return (
        <section id="hero" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden px-5 py-24 @container" style={bgStyle}>
            {showDecorations && (
                <>
                    <GradientOrbs primaryColor={primaryColor} variant="cool" />
                    <NoiseTexture opacity={0.025} />
                </>
            )}

            <div className="relative max-w-3xl mx-auto text-center z-10">
                {showBadge && badgeText && (
                    <HeroBadge text={badgeText} primaryColor={primaryColor} />
                )}
                <h1
                    className={`text-4xl @sm:text-5xl @md:text-6xl font-extrabold text-gray-900 mb-6 tracking-[-0.025em] leading-[1.08] ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('hero', 'headline', e.currentTarget.textContent || '')}
                >
                    {content.headline}
                </h1>
                <p
                    className={`text-lg @md:text-xl text-gray-500 mb-12 max-w-2xl mx-auto leading-relaxed ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('hero', 'subheadline', e.currentTarget.textContent || '')}
                >
                    {content.subheadline}
                </p>

                <div className="mb-10">
                    <HeroActionArea
                        content={content}
                        primaryColor={primaryColor}
                        isEditing={isEditing}
                        onEdit={onEdit}
                        sections={sections || []}
                        layoutConfig={layoutConfig}
                        buttonStyle={buttonStyle}
                        onStyleChange={onButtonStyleChange}
                        onCtaClick={onCtaClick}
                        formConfig={formConfig}
                        onSubmit={onSubmit}
                    />
                </div>

                {showTrustBar && (
                    <div className="max-w-xl mx-auto">
                        <TrustBar primaryColor={primaryColor} text={layoutConfig?.trustBarText} avatarSeed={layoutConfig?.trustBarAvatarSeed} />
                    </div>
                )}
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 2: LEFT ALIGNED
// ============================================================================
function HeroLeftAligned({
    content,
    primaryColor,
    sizeClasses,
    isEditing,
    onEdit,
    sections,
    layoutConfig,
    buttonStyle,
    onButtonStyleChange,
    badgeText = 'Beta',
    showBadge = false,
    onCtaClick,
    formConfig,
    onSubmit
}: Omit<HeroSectionProps, 'layout'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-4' : '';
    const imagePosition = layoutConfig?.imagePosition || 'right';
    const showTrustBar = layoutConfig?.showTrustBar !== false;
    const showDecorations = layoutConfig?.showDecorations !== false;
    const bgStyle = getHeroBgStyle(primaryColor, layoutConfig, 'left-aligned');

    const contentSection = (
        <div className="flex-1 flex flex-col justify-center text-center @3xl:text-left px-5 @md:px-8 @3xl:px-0">
            {showBadge && badgeText && (
                <div className="flex justify-center @3xl:justify-start">
                    <HeroBadge text={badgeText} primaryColor={primaryColor} />
                </div>
            )}
            <h1
                className={`text-4xl @sm:text-5xl @md:text-6xl font-extrabold text-gray-900 mb-6 tracking-[-0.025em] leading-[1.08] ${editClass}`}
                contentEditable={isEditing}
                suppressContentEditableWarning
                onBlur={(e) => onEdit('hero', 'headline', e.currentTarget.textContent || '')}
            >
                {content.headline}
            </h1>
            <p
                className={`text-lg text-gray-500 mb-10 max-w-xl mx-auto @3xl:mx-0 leading-relaxed ${editClass}`}
                contentEditable={isEditing}
                suppressContentEditableWarning
                onBlur={(e) => onEdit('hero', 'subheadline', e.currentTarget.textContent || '')}
            >
                {content.subheadline}
            </p>

            <div className="flex flex-col items-center @3xl:items-start mb-10">
                <HeroActionArea
                    content={content}
                    primaryColor={primaryColor}
                    isEditing={isEditing}
                    onEdit={onEdit}
                    sections={sections || []}
                    layoutConfig={layoutConfig}
                    buttonStyle={buttonStyle}
                    onStyleChange={onButtonStyleChange}
                    onCtaClick={onCtaClick}
                    formConfig={formConfig}
                    onSubmit={onSubmit}
                />
            </div>

            {showTrustBar && (
                <div className="flex justify-center @3xl:justify-start">
                    <TrustBar primaryColor={primaryColor} text={layoutConfig?.trustBarText} avatarSeed={layoutConfig?.trustBarAvatarSeed} />
                </div>
            )}
        </div>
    );

    const imageSection = (
        <div className="flex-1 flex items-center justify-center w-full px-4 @md:px-8 @3xl:px-0 @3xl:pl-8">
            {layoutConfig?.imageUrl ? (
                <img
                    src={layoutConfig.imageUrl}
                    alt="Hero visual"
                    className="w-full max-w-md @3xl:max-w-full max-h-[400px] @3xl:max-h-[520px] object-contain rounded-2xl shadow-2xl"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
            ) : (
                <div className="w-full max-w-md @3xl:max-w-lg">
                    <ProductMockupPlaceholder primaryColor={primaryColor} />
                </div>
            )}
        </div>
    );

    return (
        <section id="hero" className="relative min-h-[85vh] flex items-center overflow-hidden py-16 @md:py-20 @3xl:py-24 @container" style={bgStyle}>
            {showDecorations && (
                <>
                    <GradientOrbs primaryColor={primaryColor} />
                    <NoiseTexture opacity={0.02} />
                </>
            )}

            <div className="max-w-7xl mx-auto w-full px-5 @3xl:px-8 relative z-10">
                <div className={`flex flex-col gap-10 @3xl:gap-16 @3xl:flex-row @3xl:items-center ${imagePosition === 'left' ? '@3xl:flex-row-reverse' : ''}`}>
                    {contentSection}
                    {imageSection}
                </div>
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 3: SPLIT 50/50
// ============================================================================
function HeroSplit({
    content,
    primaryColor,
    sizeClasses,
    isEditing,
    onEdit,
    sections,
    layoutConfig,
    buttonStyle,
    onButtonStyleChange,
    badgeText = 'Beta',
    showBadge = false,
    onCtaClick,
    formConfig,
    onSubmit
}: Omit<HeroSectionProps, 'layout'>) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-4' : '';
    const imagePosition = layoutConfig?.imagePosition || 'right';
    const showTrustBar = layoutConfig?.showTrustBar !== false;
    const bgStyle = getHeroBgStyle(primaryColor, layoutConfig, 'split');

    const contentSection = (
        <div className="flex-1 flex flex-col justify-center px-6 py-14 @md:px-12 @md:py-20 @3xl:py-24 @3xl:pl-16 @3xl:pr-12 text-center @3xl:text-left" style={bgStyle}>
            {showBadge && badgeText && (
                <div className="flex justify-center @3xl:justify-start">
                    <HeroBadge text={badgeText} primaryColor={primaryColor} />
                </div>
            )}
            <h1
                className={`text-3xl @sm:text-4xl @md:text-5xl font-extrabold text-gray-900 mb-6 tracking-[-0.025em] leading-[1.1] ${editClass}`}
                contentEditable={isEditing}
                suppressContentEditableWarning
                onBlur={(e) => onEdit('hero', 'headline', e.currentTarget.textContent || '')}
            >
                {content.headline}
            </h1>
            <p
                className={`text-lg text-gray-500 mb-10 max-w-lg mx-auto @3xl:mx-0 leading-relaxed ${editClass}`}
                contentEditable={isEditing}
                suppressContentEditableWarning
                onBlur={(e) => onEdit('hero', 'subheadline', e.currentTarget.textContent || '')}
            >
                {content.subheadline}
            </p>

            <div className="flex flex-col gap-6 items-center @3xl:items-start">
                <HeroActionArea
                    content={content}
                    primaryColor={primaryColor}
                    isEditing={isEditing}
                    onEdit={onEdit}
                    sections={sections || []}
                    layoutConfig={layoutConfig}
                    buttonStyle={buttonStyle}
                    onStyleChange={onButtonStyleChange}
                    onCtaClick={onCtaClick}
                    formConfig={formConfig}
                    onSubmit={onSubmit}
                />

                {showTrustBar && (
                    <TrustBar primaryColor={primaryColor} text={layoutConfig?.trustBarText} avatarSeed={layoutConfig?.trustBarAvatarSeed} />
                )}
            </div>
        </div>
    );

    const imageSection = (
        <div className="flex-1 flex items-center justify-center p-6 @md:p-10 @3xl:py-12 @3xl:pr-12 w-full">
            {layoutConfig?.imageUrl ? (
                <div className="relative w-full max-h-[550px]">
                    <img
                        src={layoutConfig.imageUrl}
                        alt="Hero visual"
                        className="w-full h-full object-contain rounded-2xl"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                </div>
            ) : (
                <div className="w-full max-w-lg">
                    <ProductMockupPlaceholder primaryColor={primaryColor} />
                </div>
            )}
        </div>
    );

    return (
        <section id="hero" className="relative min-h-[600px] @3xl:min-h-[90vh] @container bg-white overflow-hidden">
            <NoiseTexture opacity={0.015} />
            <div className={`relative z-10 flex flex-col @3xl:flex-row min-h-[600px] @3xl:min-h-[90vh] ${imagePosition === 'left' ? '@3xl:flex-row-reverse' : ''}`}>
                {contentSection}
                {imageSection}
            </div>
        </section>
    );
}

// ============================================================================
// LAYOUT 4: GRADIENT BOLD (Dark cinematic)
// ============================================================================
function HeroGradientBold({
    content,
    primaryColor,
    sizeClasses,
    isEditing,
    onEdit,
    sections,
    layoutConfig,
    buttonStyle,
    onButtonStyleChange,
    badgeText = 'Beta',
    showBadge = false,
    onCtaClick,
    formConfig,
    onSubmit
}: Omit<HeroSectionProps, 'layout'> & { layoutConfig?: SectionLayoutConfig }) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-4' : '';
    const showTrustBar = layoutConfig?.showTrustBar !== false;
    const showDecorations = layoutConfig?.showDecorations !== false;
    const bgStyle = getHeroBgStyle(primaryColor, layoutConfig, 'gradient-bold');
    const lightMode = isLightMode('gradient-bold', layoutConfig?.heroBackground);
    const textColor = lightMode ? 'text-white' : 'text-gray-900';
    const subTextColor = lightMode ? 'text-white/70' : 'text-gray-500';
    const badgeVariant = lightMode ? 'light' : 'default';
    const trustVariant = lightMode ? 'light' : 'default';
    const ctaVariant = lightMode ? 'light' : 'default';

    return (
        <section
            id="hero"
            className="relative min-h-[100vh] flex items-center justify-center overflow-hidden px-5 py-28 @container"
            style={bgStyle}
        >
            {/* Dark mode decorations — lens flares + star field */}
            {showDecorations && lightMode && (
                <>
                    <GradientOrbs primaryColor={primaryColor} variant="dark" />
                    {/* Star field / particle dots */}
                    <DotGrid color="white" spacing={32} size={1} opacity={0.06} />
                    {/* Vignette overlay */}
                    <div className="absolute inset-0 pointer-events-none" style={{
                        background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)',
                    }} />
                    {/* Top edge glow line */}
                    <div className="absolute top-0 left-1/4 right-1/4 h-px pointer-events-none" style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
                    }} />
                </>
            )}

            {showDecorations && !lightMode && (
                <>
                    <GradientOrbs primaryColor={primaryColor} />
                    <NoiseTexture opacity={0.025} />
                </>
            )}

            <div className="relative max-w-3xl mx-auto text-center z-10">
                {showBadge && badgeText && (
                    <HeroBadge text={badgeText} primaryColor={primaryColor} variant={badgeVariant} />
                )}

                <h1
                    className={`text-4xl @sm:text-5xl @md:text-6xl @lg:text-7xl font-black ${textColor} mb-7 tracking-[-0.03em] leading-[1.05] ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('hero', 'headline', e.currentTarget.textContent || '')}
                >
                    {content.headline}
                </h1>

                <p
                    className={`text-lg @md:text-xl ${subTextColor} mb-14 max-w-2xl mx-auto leading-relaxed ${editClass}`}
                    contentEditable={isEditing}
                    suppressContentEditableWarning
                    onBlur={(e) => onEdit('hero', 'subheadline', e.currentTarget.textContent || '')}
                >
                    {content.subheadline}
                </p>

                <div className="mb-12">
                    <HeroActionArea
                        content={content}
                        primaryColor={primaryColor}
                        isEditing={isEditing}
                        onEdit={onEdit}
                        sections={sections || []}
                        layoutConfig={layoutConfig}
                        buttonStyle={buttonStyle}
                        onStyleChange={onButtonStyleChange}
                        onCtaClick={onCtaClick}
                        variant={ctaVariant}
                        formConfig={formConfig}
                        onSubmit={onSubmit}
                    />
                </div>

                {showTrustBar && (
                    <div className="max-w-xl mx-auto">
                        <TrustBar primaryColor={primaryColor} variant={trustVariant} text={layoutConfig?.trustBarText} avatarSeed={layoutConfig?.trustBarAvatarSeed} />
                    </div>
                )}
            </div>
        </section>
    );
}

// ============================================================================
// MAIN EXPORT WITH LAYOUT SWITCHING
// ============================================================================
export function HeroSection({
    content,
    primaryColor,
    sizeClasses,
    isEditing,
    onEdit,
    sections = [],
    layout = 'centered',
    layoutConfig,
    buttonStyle,
    onButtonStyleChange,
    badgeText,
    showBadge,
    onCtaClick,
    formConfig,
    onSubmit
}: HeroSectionProps) {
    const commonProps = {
        content,
        primaryColor,
        sizeClasses,
        isEditing,
        onEdit,
        sections,
        layoutConfig,
        buttonStyle,
        onButtonStyleChange,
        badgeText,
        showBadge,
        onCtaClick,
        formConfig,
        onSubmit,
    };

    switch (layout) {
        case 'left-aligned':
            return <HeroLeftAligned {...commonProps} />;
        case 'split':
            return <HeroSplit {...commonProps} />;
        case 'gradient-bold':
            return <HeroGradientBold {...commonProps} />;
        case 'centered':
        default:
            return <HeroCentered {...commonProps} />;
    }
}
